package com.sweetlime.bingeamigo.presentation.features.review

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.bingeamigo.model.ContentSummary
import com.sweetlime.bingeamigo.model.LibraryEpisode
import com.sweetlime.bingeamigo.model.LibraryMovie
import com.sweetlime.bingeamigo.model.LibraryTvShow
import com.sweetlime.bingeamigo.model.UserReview
import com.sweetlime.bingeamigo.model.types.ActivityType
import com.sweetlime.bingeamigo.model.types.ContentType
import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.repository.ActivityRepository
import com.sweetlime.bingeamigo.repository.AuthRepository
import com.sweetlime.bingeamigo.repository.LibraryRepository
import com.sweetlime.bingeamigo.repository.ReviewRepository
import com.sweetlime.bingeamigo.repository.UserRepository
import com.sweetlime.bingeamigo.service.XpService
import dev.gitlive.firebase.firestore.Timestamp
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class ReviewUiState(
    val existingReview: UserReview? = null,
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val saved: Boolean = false,
    val error: String? = null
)

class ReviewViewModel(
    private val reviewRepository: ReviewRepository,
    private val libraryRepository: LibraryRepository,
    private val authRepository: AuthRepository,
    private val userRepository: UserRepository,
    private val activityRepository: ActivityRepository,
    private val xpService: XpService
) : ViewModel() {

    private val _uiState = MutableStateFlow(ReviewUiState())
    val uiState: StateFlow<ReviewUiState> = _uiState.asStateFlow()

    private val _contentSummary = MutableStateFlow(ContentSummary())
    val contentSummary: StateFlow<ContentSummary> = _contentSummary.asStateFlow()

    private var summaryJob: Job? = null

    fun load(contentType: ContentType, contentId: String) {
        val uid = authRepository.currentUserId() ?: return
        _uiState.value = ReviewUiState(isLoading = true)
        viewModelScope.launch {
            val review = reviewRepository.getUserReview(uid, contentType.name, contentId)
            _uiState.value = ReviewUiState(existingReview = review)
        }
        summaryJob?.cancel()
        summaryJob = viewModelScope.launch {
            reviewRepository.getContentSummary(contentType.name, contentId)
                .collect { _contentSummary.value = it }
        }
    }

    fun reset() {
        _uiState.value = _uiState.value.copy(isLoading = false, isSaving = false, saved = false, error = null)
    }

    @OptIn(ExperimentalUuidApi::class, ExperimentalTime::class)
    fun submitMovieReview(libraryMovie: LibraryMovie, rating: Double, reviewText: String?) {
        val uid = authRepository.currentUserId() ?: return
        val previous = _uiState.value.existingReview?.rating
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)

        viewModelScope.launch {
            runCatching {
                val user = userRepository.getUserProfile(uid)
                val review = UserReview(
                    uid = uid,
                    contentType = ContentType.MOVIE.name,
                    contentId = libraryMovie.id.toString(),
                    rating = rating,
                    review = reviewText?.takeIf { it.isNotBlank() },
                    displayName = user?.username.orEmpty(),
                    userAvatar = user?.avatarUrl.orEmpty(),
                    contentTitle = libraryMovie.movie.title,
                    contentPosterPath = libraryMovie.movie.posterPath.orEmpty(),
                    timestamp = Timestamp.now()
                )
                reviewRepository.submitReview(review, previous)
                libraryRepository.rateMovie(libraryMovie, rating, reviewText?.takeIf { it.isNotBlank() })

                activityRepository.publishActivity(
                    UserActivity(
                        id = Uuid.random().toString(),
                        userId = uid,
                        username = user?.username.orEmpty(),
                        userAvatar = user?.avatarUrl.orEmpty(),
                        type = ActivityType.RATE_MOVIE.value,
                        movieId = libraryMovie.id,
                        movieTitle = libraryMovie.movie.title,
                        moviePoster = libraryMovie.movie.posterPath.orEmpty(),
                        rating = rating,
                        timestamp = Clock.System.now().toEpochMilliseconds()
                    )
                )

                if (previous == null) {
                    xpService.awardXp(uid, XpService.ACTION_RATED_MOVIE, libraryMovie.id.toString(), XpService.XP_RATED_MOVIE)
                }
                val trimmed = reviewText?.trim().orEmpty()
                if (trimmed.length >= 50) {
                    xpService.awardXp(uid, XpService.ACTION_REVIEWED_MOVIE, libraryMovie.id.toString(), XpService.XP_REVIEWED_MOVIE)
                }

                _uiState.value = ReviewUiState(existingReview = review, saved = true)
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false, error = "Failed to save review")
            }
        }
    }

    @OptIn(ExperimentalUuidApi::class, ExperimentalTime::class)
    fun submitShowReview(libraryTvShow: LibraryTvShow, rating: Double, reviewText: String?) {
        val uid = authRepository.currentUserId() ?: return
        val previous = _uiState.value.existingReview?.rating
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)

        viewModelScope.launch {
            runCatching {
                val user = userRepository.getUserProfile(uid)
                val review = UserReview(
                    uid = uid,
                    contentType = ContentType.SHOW.name,
                    contentId = libraryTvShow.id.toString(),
                    rating = rating,
                    review = reviewText?.takeIf { it.isNotBlank() },
                    displayName = user?.username.orEmpty(),
                    userAvatar = user?.avatarUrl.orEmpty(),
                    contentTitle = libraryTvShow.tvShow.name,
                    contentPosterPath = libraryTvShow.tvShow.posterPath.orEmpty(),
                    timestamp = Timestamp.now()
                )
                reviewRepository.submitReview(review, previous)
                libraryRepository.rateTvShow(libraryTvShow, rating, reviewText?.takeIf { it.isNotBlank() })

                activityRepository.publishActivity(
                    UserActivity(
                        id = Uuid.random().toString(),
                        userId = uid,
                        username = user?.username.orEmpty(),
                        userAvatar = user?.avatarUrl.orEmpty(),
                        type = ActivityType.RATE_SHOW.value,
                        tvShowId = libraryTvShow.id,
                        tvShowTitle = libraryTvShow.tvShow.name,
                        tvShowPoster = libraryTvShow.tvShow.posterPath.orEmpty(),
                        rating = rating,
                        timestamp = Clock.System.now().toEpochMilliseconds()
                    )
                )

                if (previous == null) {
                    xpService.awardXp(uid, XpService.ACTION_RATED_SHOW, libraryTvShow.id.toString(), XpService.XP_RATED_SHOW)
                }
                val trimmed = reviewText?.trim().orEmpty()
                if (trimmed.length >= 50) {
                    xpService.awardXp(uid, XpService.ACTION_REVIEWED_SHOW, libraryTvShow.id.toString(), XpService.XP_REVIEWED_SHOW)
                }

                _uiState.value = ReviewUiState(existingReview = review, saved = true)
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false, error = "Failed to save review")
            }
        }
    }

    @OptIn(ExperimentalUuidApi::class, ExperimentalTime::class)
    fun submitEpisodeReview(
        libraryTvShow: LibraryTvShow,
        episode: LibraryEpisode,
        episodeTitle: String,
        episodePosterPath: String,
        rating: Double,
        reviewText: String?
    ) {
        val uid = authRepository.currentUserId() ?: return
        val previous = _uiState.value.existingReview?.rating
        val contentId = "${libraryTvShow.id}:${episode.id}"
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)

        viewModelScope.launch {
            runCatching {
                val user = userRepository.getUserProfile(uid)
                val review = UserReview(
                    uid = uid,
                    contentType = ContentType.EPISODE.name,
                    contentId = contentId,
                    rating = rating,
                    review = reviewText?.takeIf { it.isNotBlank() },
                    displayName = user?.username.orEmpty(),
                    userAvatar = user?.avatarUrl.orEmpty(),
                    contentTitle = episodeTitle,
                    contentPosterPath = episodePosterPath,
                    timestamp = Timestamp.now()
                )
                reviewRepository.submitReview(review, previous)
                libraryRepository.rateEpisode(libraryTvShow, episode.id, rating, reviewText?.takeIf { it.isNotBlank() })

                activityRepository.publishActivity(
                    UserActivity(
                        id = Uuid.random().toString(),
                        userId = uid,
                        username = user?.username.orEmpty(),
                        userAvatar = user?.avatarUrl.orEmpty(),
                        type = ActivityType.RATE_EPISODE.value,
                        tvShowId = libraryTvShow.id,
                        tvShowTitle = libraryTvShow.tvShow.name,
                        tvShowPoster = libraryTvShow.tvShow.posterPath.orEmpty(),
                        seasonNumber = episode.seasonNumber,
                        episodeNumber = episode.episodeNumber,
                        episodeId = episode.id,
                        episodeName = episodeTitle,
                        episodeStillPath = episodePosterPath,
                        rating = rating,
                        timestamp = Clock.System.now().toEpochMilliseconds()
                    )
                )

                _uiState.value = ReviewUiState(existingReview = review, saved = true)
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false, error = "Failed to save review")
            }
        }
    }
}
